import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import WelcomeStep from './components/WelcomeStep';
import InfoStep from './components/InfoStep';
import ReviewStep from './components/ReviewStep';
import GiftsStep from './components/GiftsStep';
import AmazonModal from './components/AmazonModal';
import AdminDashboard from './components/AdminDashboard';
import {
  createSubmission,
  updateReviewData,
  trackAmazonVisit,
  trackGiftsClaimed
} from './utils/supabase';

/**
 * CustomerFlow Component
 * Manages the 4-step customer journey
 */
function CustomerFlow() {
  // Current step state (1-4)
  const [currentStep, setCurrentStep] = useState(1);

  // Customer data
  const [customerData, setCustomerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    optInSurveys: false
  });

  // Supabase submission ID
  const [submissionId, setSubmissionId] = useState(null);

  // Modal state
  const [showAmazonModal, setShowAmazonModal] = useState(false);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Amazon review URL - can be customized via env variable
  // NOTE: NO www subdomain for deep linking to work on mobile!
  const amazonReviewUrl =
    process.env.REACT_APP_AMAZON_REVIEW_URL ||
    'https://amazon.fr/review/create-review';

  /**
   * Handle Step 1 -> Step 2
   */
  const handleWelcomeContinue = () => {
    setCurrentStep(2);
  };

  /**
   * Handle Step 2 -> Step 3
   * Save customer information to Supabase
   */
  const handleInfoContinue = async (formData) => {
    setIsLoading(true);
    setCustomerData(formData);

    try {
      const id = await createSubmission(formData);
      setSubmissionId(id);
      // Store in localStorage so GiftsStep can access it
      localStorage.setItem('submissionId', id);
      console.log('✅ Submission created with ID:', id);
      setCurrentStep(3);
    } catch (error) {
      alert('Error saving your information. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle review generation
   * Track that user generated a review
   */
  const handleReviewGenerated = async (reviewData) => {
    if (submissionId) {
      try {
        await updateReviewData(submissionId, reviewData);
      } catch (error) {
        console.error('Error tracking review generation:', error);
      }
    }
  };

  /**
   * Handle Amazon redirect
   * Show modal first, then redirect
   */
  const handleAmazonRedirect = () => {
    setShowAmazonModal(true);
  };

  /**
   * Confirm Amazon redirect
   * Track visit and open Amazon in new tab
   */
  const handleConfirmAmazonRedirect = async () => {
    // Open Amazon IMMEDIATELY - must be first to avoid Safari popup blocker
    // Safari blocks window.open() if called after async operations
    window.open(amazonReviewUrl, '_blank');

    // Close modal
    setShowAmazonModal(false);

    // Track visit after opening (non-blocking)
    if (submissionId) {
      try {
        await trackAmazonVisit(submissionId);
      } catch (error) {
        console.error('Error tracking Amazon visit:', error);
      }
    }
  };

  /**
   * Handle claim gifts
   * Go directly to Step 4
   */
  const handleClaimGifts = () => {
    setCurrentStep(4);
  };

  /**
   * Handle skip to gifts (from InfoStep)
   * Go directly to Step 4 without saving customer data
   */
  const handleSkipToGifts = () => {
    setCurrentStep(4);
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  /**
   * Handle gifts claimed
   * Track that user viewed the gifts page
   */
  const handleGiftsClaimed = async () => {
    if (submissionId) {
      try {
        await trackGiftsClaimed(submissionId);
      } catch (error) {
        console.error('Error tracking gifts claimed:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
          <div className="text-center">
            <svg
              className="animate-spin h-12 w-12 text-black mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-lg font-light">Loading...</p>
          </div>
        </div>
      )}

      {/* Step 1: Welcome */}
      {currentStep === 1 && (
        <WelcomeStep
          onContinue={handleWelcomeContinue}
          onSkipToGifts={handleSkipToGifts}
        />
      )}

      {/* Step 2: Customer Info */}
      {currentStep === 2 && (
        <InfoStep
          onContinue={handleInfoContinue}
          onBack={handleBack}
          onSkipToGifts={handleSkipToGifts}
          initialData={customerData}
        />
      )}

      {/* Step 3: Review Generation */}
      {currentStep === 3 && (
        <ReviewStep
          onAmazonRedirect={handleAmazonRedirect}
          onClaimGifts={handleClaimGifts}
          onReviewGenerated={handleReviewGenerated}
          onBack={handleBack}
        />
      )}

      {/* Step 4: Gift Delivery */}
      {currentStep === 4 && (
        <GiftsStep onGiftsClaimed={handleGiftsClaimed} onBack={handleBack} />
      )}

      {/* Amazon Modal */}
      <AmazonModal
        isOpen={showAmazonModal}
        onClose={() => setShowAmazonModal(false)}
        onConfirm={handleConfirmAmazonRedirect}
      />
    </div>
  );
}

/**
 * AdminLogin Component
 * Simple password-based authentication for admin access
 */
function AdminLogin() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Admin password from environment variable or default
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
    } else {
      setError('Incorrect password');
    }
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  if (isAuthenticated) {
    return <AdminDashboard />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-gray-50">
      <div className="max-w-md w-full bg-white p-8 border border-gray-200">
        <h2 className="text-3xl font-light tracking-wide text-center mb-8">
          Admin Login
        </h2>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-light tracking-wide mb-2 text-gray-700"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-black transition-colors"
              required
            />
            {error && (
              <p className="mt-2 text-sm text-red-500">{error}</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full px-8 py-4 bg-black text-white text-lg font-light tracking-wide
                     hover:bg-gray-800 transition-all duration-300
                     focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            Login
          </button>

          <button
            type="button"
            onClick={handleBackToMain}
            className="w-full px-8 py-3 border border-gray-300 bg-white text-black text-sm font-light tracking-wide
                     hover:border-black transition-all duration-300
                     focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            Back to Main
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * DirectGiftsPage Component
 * Standalone gifts page accessible via direct link
 */
function DirectGiftsPage() {
  return (
    <div className="min-h-screen bg-white text-black">
      <GiftsStep />
    </div>
  );
}

/**
 * Main App Component with Routing
 */
function App() {
  return (
    <Routes>
      <Route path="/" element={<CustomerFlow />} />
      <Route path="/gifts" element={<DirectGiftsPage />} />
      <Route path="/admin" element={<AdminLogin />} />
    </Routes>
  );
}

export default App;
